import { useId } from "react"
import { cn } from "@/lib/utils"

/**
 * The Forge brand mark, "Shade" (Snowball SEO): one snowball with a soft
 * shadow on a blue tile.
 *
 * Geometry matches public/favicon.svg and the iOS app icon exactly (master
 * vector: forge-ios `brand/app-icon-shade.svg`). `tile={false}` draws the
 * snowball alone, for a surface that is already blue.
 *
 * Decorative by default: it is always paired with the "Forge" wordmark, so it
 * is hidden from assistive tech rather than repeating the name.
 */
export function ForgeMark({
  size = 32,
  tile = true,
  className,
}: {
  size?: number
  /** Draw the blue rounded tile behind the mark. */
  tile?: boolean
  className?: string
}) {
  // Gradient and clip ids must be unique per instance: the sidebar and the
  // login page can both have a mark in the document.
  const id = useId()
  const tileId = `${id}-tile`
  const ballId = `${id}-ball`

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 1024 1024"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      className={cn("shrink-0", className)}
    >
      <defs>
        <linearGradient id={tileId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3563d6" />
          <stop offset="1" stopColor="#1d3d9c" />
        </linearGradient>
        <clipPath id={ballId}>
          <circle cx="512" cy="512" r="250" />
        </clipPath>
      </defs>

      {tile && <rect width="1024" height="1024" rx="224" ry="224" fill={`url(#${tileId})`} />}

      {/* the shadow is the whole ball; the lit face is a second circle, up and left, clipped to it */}
      <circle cx="512" cy="512" r="250" fill="#b4c9f0" />
      <circle cx="462" cy="462" r="240" fill="#eef2f7" clipPath={`url(#${ballId})`} />
    </svg>
  )
}
